import { type Currency } from "~/api/products/types";
import { type ReceiptReport } from "~/hooks/queries/reports";

import {
  type CashDrawerData,
  type CategoryRow,
  type DepositFeeRow,
  type DepositFeeSummaryData,
  type DiscountItem,
  type PaymentItem,
  type ReportSectionsData,
  type RefundItem,
  type SalesSummaryData,
  type UserRow,
  type VatRow,
  type VatSummaryData
} from "./sections/types";

/**
 * A session with no sales carries no per-currency entries at all, so the
 * receipt alone cannot tell the currency. Callers that know the till pass its
 * configured currency as `fallbackCurrency`; "DKK" remains the last resort.
 */
export function getFirstCurrency(
  receipt: ReceiptReport,
  fallbackCurrency?: Currency
): Currency {
  return receipt.totalSales?.values[0]?.currency || fallbackCurrency || "DKK";
}

/** Rounds a major-unit amount to integer cents. */
export const toCents = (value: number): number => Math.round(value * 100);

/** Reads the first currency value from a report field and converts to cents. */
export const fieldToCents = (
  field: { values?: { value: number }[] } | undefined
): number => toCents(field?.values?.[0]?.value ?? 0);

/**
 * Rebuilds the full sales chain from a known NET figure (after discounts and
 * refunds). `discountsCents` and `refundsCents` are expected to be negative.
 */
export function buildSalesSummaryFromNet(
  netSales: number,
  discountsCents: number,
  refundsCents: number,
  currency: Currency
): SalesSummaryData {
  const salesAfterDiscounts = netSales - refundsCents;
  const grossSales = salesAfterDiscounts + Math.abs(discountsCents);

  return {
    grossSales,
    discounts: discountsCents,
    salesAfterDiscounts,
    refunds: refundsCents,
    netSales,
    currency
  };
}

export function buildSalesSummary(
  receipt: ReceiptReport,
  currency: Currency
): SalesSummaryData {
  return buildSalesSummaryFromNet(
    fieldToCents(receipt.totalSales),
    fieldToCents(receipt.discounts),
    fieldToCents(receipt.refund),
    currency
  );
}

export function buildDiscountBreakdown(receipt: ReceiptReport): {
  items: DiscountItem[];
  total: number;
} {
  const discountsSection = receipt.sections.discounts;
  const items: DiscountItem[] = [];

  if (discountsSection) {
    Object.entries(discountsSection).forEach(([key, val]) => {
      if (typeof val === "string" || key === "sectionTitle") return;
      const firstValue = val.values[0];
      if (!firstValue) return;
      items.push({
        name: val.label,
        amount: toCents(firstValue.value)
      });
    });
  }

  const total = fieldToCents(receipt.discounts);

  return { items, total };
}

export function buildRefundBreakdown(receipt: ReceiptReport): {
  breakdown: RefundItem[];
  totalRefunds: number;
} {
  const refundsSection = receipt.sections.refunds;
  const breakdown: RefundItem[] = [];

  if (refundsSection) {
    Object.entries(refundsSection).forEach(([key, val]) => {
      if (typeof val === "string" || key === "sectionTitle") return;
      const firstValue = val.values[0];
      if (!firstValue) return;
      breakdown.push({
        label: val.label,
        amount: toCents(firstValue.value)
      });
    });
  }

  const totalRefunds = fieldToCents(receipt.refund);

  return { breakdown, totalRefunds };
}

/**
 * Deposit (DRS) fees per scheme, excl. VAT: the deposit's VAT already shows in
 * the VAT summary, so counting it here would double it.
 *
 * The backend's own `total` row is ignored in favour of totals summed from the
 * visible rows — a TOTAL that does not add up to the lines above it is a
 * support ticket.
 */
export function buildDepositFees(
  receipt: ReceiptReport
): Omit<DepositFeeSummaryData, "currency"> {
  const depositsSection = receipt.sections.deposits;
  const rows: DepositFeeRow[] = [];

  if (depositsSection) {
    Object.entries(depositsSection).forEach(([key, val]) => {
      if (typeof val === "string" || key === "sectionTitle" || key === "total")
        return;

      const firstValue = val.values[0];
      if (!firstValue) return;

      rows.push({
        // Two schemes can share a label, so the key is what keeps them apart.
        id: key,
        schemeName: val.label,
        feeExclVat: toCents(firstValue.unitFeeExclVat ?? 0),
        netQuantity: firstValue.netQuantity,
        netAmount: toCents(firstValue.value)
      });
    });
  }

  const totals = {
    netQuantity: rows.reduce((sum, r) => sum + r.netQuantity, 0),
    netAmount: rows.reduce((sum, r) => sum + r.netAmount, 0)
  };

  return { rows, totals };
}

export function buildVatTableData(
  receipt: ReceiptReport,
  fallbackCurrency?: Currency
): VatSummaryData {
  const firstCurrency = receipt.standardVAT?.values[0];
  const currency = firstCurrency?.currency || fallbackCurrency || "DKK";

  if (!firstCurrency || firstCurrency.values.length === 0) {
    return {
      rows: [],
      totals: {
        percentage: 0,
        salesExclVat: 0,
        vatAmount: 0,
        salesInclVat: 0
      },
      currency,
      hasFullData: false
    };
  }

  const hasFullData = firstCurrency.values.some(
    (v) => v.salesExclVat !== undefined && v.salesInclVat !== undefined
  );

  const rows: VatRow[] = firstCurrency.values.map(
    ({ percentage, value, salesExclVat, salesInclVat }) => {
      const vatAmountCents = toCents(value);

      let salesExclVatCents: number;
      let salesInclVatCents: number;

      if (salesExclVat !== undefined && salesInclVat !== undefined) {
        salesExclVatCents = toCents(salesExclVat ?? 0);
        salesInclVatCents = toCents(salesInclVat ?? 0);
      } else {
        const rate = percentage / 100;
        if (rate > 0) {
          salesExclVatCents = Math.round(vatAmountCents / rate);
          salesInclVatCents = salesExclVatCents + vatAmountCents;
        } else {
          salesExclVatCents = 0;
          salesInclVatCents = 0;
        }
      }

      return {
        percentage,
        salesExclVat: salesExclVatCents,
        vatAmount: vatAmountCents,
        salesInclVat: salesInclVatCents
      };
    }
  );

  const totals: VatRow = {
    percentage: 0,
    salesExclVat: rows.reduce((sum, r) => sum + r.salesExclVat, 0),
    vatAmount: rows.reduce((sum, r) => sum + r.vatAmount, 0),
    salesInclVat: rows.reduce((sum, r) => sum + r.salesInclVat, 0)
  };

  return { rows, totals, currency, hasFullData };
}

export function buildPaymentSummary(receipt: ReceiptReport): {
  items: PaymentItem[];
  total: number;
} {
  const methods = receipt.sections.paymentsMethods;
  const balanceCents = fieldToCents(methods.credit);

  const items: PaymentItem[] = [
    { label: "Cash", amount: fieldToCents(methods.cash) },
    { label: "Card", amount: fieldToCents(methods.card) },
    { label: "MobilePay", amount: fieldToCents(methods.mobilepay) },
    { label: "Swish", amount: fieldToCents(methods.swish) },
    { label: "Invoice", amount: fieldToCents(methods.billing) },
    { label: "Balance", amount: balanceCents }
  ];

  // `methods.total` excludes balance (credit), so add it for the displayed total.
  // TODO(PWEB-150): once the backend folds credit into `methods.total`, drop the
  // `+ balanceCents` workaround here.
  return { items, total: fieldToCents(methods.total) + balanceCents };
}

type NamedAmountRow = { name: string; amount: number; productCount: number };

/** Shared builder for the category and user breakdown tables (same shape). */
function buildNamedAmountRows(
  section: ReceiptReport["sections"]["categories"]
): { rows: NamedAmountRow[]; totals: NamedAmountRow } {
  const rows: NamedAmountRow[] = [];

  Object.entries(section).forEach(([key, val]) => {
    if (typeof val === "string" || key === "sectionTitle" || key === "all")
      return;

    const firstValue = val.values[0];
    if (!firstValue) return;

    rows.push({
      name: val.label,
      amount: toCents(firstValue.value),
      productCount: firstValue.unitSold ?? 0
    });
  });

  const totals: NamedAmountRow = {
    name: "TOTAL",
    amount: rows.reduce((sum, r) => sum + r.amount, 0),
    productCount: rows.reduce((sum, r) => sum + r.productCount, 0)
  };

  return { rows, totals };
}

export function buildCategories(receipt: ReceiptReport): {
  rows: CategoryRow[];
  totals: CategoryRow;
} {
  return buildNamedAmountRows(receipt.sections.categories);
}

export function buildUsers(receipt: ReceiptReport): {
  rows: UserRow[];
  totals: UserRow;
} {
  return buildNamedAmountRows(receipt.sections.profiles);
}

/** Sums cash refunds, matching the stable provider key (not the label). */
export function sumCashRefunds(receipt: ReceiptReport): number {
  const refundsSection = receipt.sections.refunds;
  if (!refundsSection) return 0;

  let cents = 0;
  Object.entries(refundsSection).forEach(([key, val]) => {
    if (key !== "cash" || typeof val === "string") return;
    cents += fieldToCents(val);
  });
  return cents;
}

/**
 * Builds the shared ReportSectionsData wrapper for both the single-till and
 * combined hooks. Only salesSummary and cashDrawer differ between them; every
 * other section is derived from `receipt` the same way.
 */
export function assembleReportSections({
  receipt,
  salesSummary,
  cashDrawer,
  currency
}: {
  receipt: ReceiptReport;
  salesSummary: SalesSummaryData;
  cashDrawer: Omit<CashDrawerData, "currency">;
  currency: Currency;
}): ReportSectionsData {
  const discountData = buildDiscountBreakdown(receipt);
  const refundData = buildRefundBreakdown(receipt);
  const vatData = buildVatTableData(receipt, currency);
  const depositFeeData = buildDepositFees(receipt);
  const paymentData = buildPaymentSummary(receipt);
  const categoriesData = buildCategories(receipt);
  const usersData = buildUsers(receipt);

  // Prefer the backend-provided average (per-order, matches other systems);
  // fall back to a client-side computation for older reports without it.
  const backendAvg = receipt.averageSales?.values[0]?.value;
  const ordersCount = Number(receipt.totalOrders?.value ?? 0);
  let avgTransactionValue = 0;
  if (typeof backendAvg === "number") {
    avgTransactionValue = toCents(backendAvg);
  } else if (ordersCount > 0) {
    avgTransactionValue = Math.round(salesSummary.netSales / ordersCount);
  }

  return {
    salesSummary,
    discounts: {
      items: discountData.items,
      total: discountData.total,
      currency
    },
    refunds: {
      totalRefunds: refundData.totalRefunds,
      breakdown: refundData.breakdown,
      currency
    },
    vatSummary: vatData,
    depositFeeSummary: { ...depositFeeData, currency },
    paymentSummary: {
      items: paymentData.items,
      total: paymentData.total,
      currency
    },
    cashDrawer: { ...cashDrawer, currency },
    transactionSummary: {
      transactions: ordersCount,
      productsSold: Number(receipt.unitSold?.value ?? 0),
      avgTransactionValue,
      discountedTransactions: receipt.discountedTransactions?.value ?? 0,
      refundedTransactions: Number(receipt.refundNumber?.value ?? 0),
      voidedTransactions: Number(receipt.cancelledNumber?.value ?? 0),
      currency
    },
    salesByCategories: {
      rows: categoriesData.rows,
      totals: categoriesData.totals,
      currency
    },
    salesPerUsers: {
      rows: usersData.rows,
      totals: usersData.totals,
      currency
    }
  };
}
